import React, {useContext, useState} from 'react';
import {useTranslation} from 'react-i18next'; // Zastępuje falcon-i18n
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {Link} from 'react-router-dom';
import {useUserContext} from '../../../context/UserContext';
import {useCartContext} from '../../../context/CartContext';
import {usePopup} from '../../../context/PopupContext';
import {addToCartEvent} from '../../../hooks/useAnalytics';
import {addToCartErrorMessage} from '../../../hooks/useAddProductToCart';
import useMountState from '../../../hooks/useMountState';
import ProductPromo from '../../UI/ProductPromo/ProductPromo';
import ChooseLink from './ChooseLink';
import AddToWishlist from './AddToWishlist';
import './ProductItem.scss';
import Labels from '../../Product/Labels/Labels';
import PriceBlock from '../../Product/PriceBlock/PriceBlock';
import {ProductProvider} from '../../../context/ProductContext';
import {BuildAbsoluteUrl} from '../../../utils/urlBuilder';
import useResponsive from '../../../hooks/useResponsive';
import AddToCompare from '../../Compare/AddToCompare';
import LastPrice30 from "./LastPrice30/LastPrice30";
import AnchorPrices from '../../Product/AnchorPrices/AnchorPrices';
import {isHR} from "../../../utils/isHr";
import hidePriceSkus from '../../../utils/hidePriceSkus';
import { getWebp } from "../../../utils/getWebp";
import Popup from '../../UI/Popup/Popup';
import LabelPopupContent from '../../UI/Popup/LabelPopupContent';
import ProductDocumentLinks from '../../Product/ProductDocumentLinks/ProductDocumentLinks';

const emmezetaPlaceholder = 'https://s3.emmezeta.hr/falcon/emmezeta-placeholder.png';
const baseClassName = 'product-item';

const AddToCartButton = ({product}) => {
    const {t} = useTranslation();
    const {user} = useUserContext();
    const {isMounted} = useMountState();
    const {handleProductAdd} = useCartContext();
    const {showAddToCartPopupMessage} = usePopup();
    const [addProductLoading, setAddProductLoading] = useState(false);

    if (!product || !product.sku) {
        return null;
    }

    const {sku, name, isOnOrder, isHasStock, days_to_delivery, canonical_url, advanced_product_inventory, __typename} =
        product;
    const isGrouped = __typename === 'GroupedProduct'; // Poprawiona logika zamiast sku.startsWith('g')
    const productUrl = canonical_url ? BuildAbsoluteUrl(canonical_url) : null;
    const enableQtyIncrements = advanced_product_inventory?.enable_qty_increments || '0';
    const qtyIncrements = advanced_product_inventory?.qty_increments || 1;
    const addProductToCart = async () => {
        setAddProductLoading(true);
        const qty = enableQtyIncrements === '1' ? Number(qtyIncrements) : 1;

        try {
            await handleProductAdd(sku, qty);
            const message = t('messagePopup.addSuccess').replace('[product]', name);
            showAddToCartPopupMessage(message, 'success', 4000);
            addToCartEvent(product, qty, productUrl);
        } catch (err) {
            const errorMessage = addToCartErrorMessage(err, t);
            showAddToCartPopupMessage(errorMessage, 'error', 4000);
        } finally {
            if (isMounted()) {
                setAddProductLoading(false);
            }
        }
    };

    if (isOnOrder && days_to_delivery === 0) {
        return (
            <button type="button" className={`${baseClassName}__cart-button`} disabled aria-label={t('cart.onlyInStoreAlt')}>
                <img src="https://s3.emmezeta.hr/falcon/OnlyInStore.png" alt={t('cart.onlyInStoreAlt')}
                     className={`${baseClassName}__cart-icon`}/>
            </button>
        );
    }

    if (!isHasStock) {
        return (
            <button type="button" className={`${baseClassName}__cart-button`} disabled aria-label={t('cart.outOfStock')}>
                <img src="https://s3.emmezeta.hr/falcon/cart-out-of-stock.webp" alt={t('cart.outOfStock')}
                     className={`${baseClassName}__cart-icon`}/>
            </button>
        );
    }

    const addToCartButton = (
        <button
            type="button"
            className={`${baseClassName}__cart-button`}
            onClick={addProductToCart}
            disabled={addProductLoading}
            name="Add to cart"
        >
            {addProductLoading ? (
                <img src="https://s3.emmezeta.hr/falcon/loaderAddProduct.gif" alt={t('cart.loadingAlt')}
                     className={`${baseClassName}__cart-icon`}/>
            ) : (
                <img src="https://s3.emmezeta.hr/falcon/cart.webp" className={`${baseClassName}__cart-icon`}/>
            )}
        </button>
    );

    return isGrouped ? <Link to={`/${canonical_url}`}>{addToCartButton}</Link> : addToCartButton;
};

AddToCartButton.propTypes = {
    product: PropTypes.shape({
        sku: PropTypes.string.isRequired,
        name: PropTypes.string,
        isOnOrder: PropTypes.bool,
        isHasStock: PropTypes.bool,
        days_to_delivery: PropTypes.number,
        canonical_url: PropTypes.string,
        advanced_product_inventory: PropTypes.shape({
            enable_qty_increments: PropTypes.string,
            qty_increments: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
        __typename: PropTypes.string,
    }),
};

const ProductItem = ({product = {}, isCms, isCategory, isQuarticon}) => {
    const {t} = useTranslation();
    const {isMobile} = useResponsive();
    const [popupGuarantee, setPopupGuarantee] = useState(null);
    const {
        name,
        image,
        thumbnailImage,
        hoverImage,
        sku,
        lifetimeStatus,
        isOnOrder,
        guarantees,
        url,
        canonical_url,
        days_to_delivery,
        __typename,
    } = product;

    const handleGuaranteeClick = (guarantee) => {
        if (isMobile && guarantee.guarantee_description) {
            setPopupGuarantee(guarantee);
        }
    };

    const closeGuaranteePopup = () => {
        setPopupGuarantee(null);
    };

    const productImage = image || emmezetaPlaceholder;
    const linkUrl = canonical_url || url;
    const hideAddToCart = __typename === 'GroupedProduct';
    const hasProductCard = product.attachments?.some(attachment => attachment.label === 'product_card');
    const hasEnergyLabels = product.emlabels?.some(label =>
        label.attribute_code?.startsWith('energetski_razred')
    );
    return (
        <li className={clsx(baseClassName, isCms && `${baseClassName}__mr-auto`, isHR && `${baseClassName}_hr`)}>
            <ProductProvider value={product} key={['sss', sku].join('__')}>
                <div className={`${baseClassName}__wrap`}>
                    <div className={`${baseClassName}__top-line`}>
                        <div className={`${baseClassName}__top-line__container`}>
                            <Labels
                                product={product}
                                slot="TL"
                                type="category"
                                excludeAttributeCodePrefix="energetski_razred"
                            />
                            {isOnOrder && days_to_delivery === 0 && (
                                <>
                                    <div className={`${baseClassName}__order-product`}>
                                        <ProductPromo type="order"
                                                      promoText={<span>{t('product.orderInTheStore')}</span>}/>
                                    </div>
                                    <div
                                        className={`${baseClassName}__order-tooltip`}>{t('product.orderInTheStoreText')}</div>
                                </>
                            )}
                        </div>
                        <div className={`${baseClassName}__top-line__container_bottom`}>
                            {guarantees?.length > 0 && (
                                <div className={`${baseClassName}__top-line__guarantees-container`}>
                                    {guarantees.map((guarantee, index) => (
                                        <React.Fragment key={`guarantee-${index}`}>
                                            {guarantee.guarantee_img_url && (
                                                <button
                                                    type="button"
                                                    className={`${baseClassName}__guarantee`}
                                                    onClick={() => handleGuaranteeClick(guarantee)}
                                                    aria-label={guarantee.guarantee_description || t('product.guaranteeInfo')}
                                                >
                                                    <img loading="lazy" src={guarantee.guarantee_img_url} alt={guarantee.guarantee_description || t('product.guaranteeInfo')} style={{width: 45}}/>
                                                </button>
                                            )}
                                            {!isMobile && guarantee.guarantee_description && (
                                                <div
                                                    className={`${baseClassName}__guarantee-description`}>{guarantee.guarantee_description}</div>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>
                            )}
                            <Popup isOpen={!!popupGuarantee} onClose={closeGuaranteePopup} title={null}>
                                <LabelPopupContent
                                    image={popupGuarantee?.guarantee_img_url}
                                    description={popupGuarantee?.guarantee_description}
                                />
                            </Popup>
                        </div>
                        <ChooseLink link={linkUrl} isQuarticon={isQuarticon} product={product}>
                            <div className={`${baseClassName}__img-wrap`}>
                                <img
                                    src={getWebp(thumbnailImage?.url) || getWebp(productImage)}
                                    className={clsx(`${baseClassName}__image`, hoverImage && 'on_hover')}
                                    alt={name || t('product.imageAlt')}
                                    loading="lazy"
                                />
                                {hoverImage?.url && (
                                    <img
                                        src={getWebp(hoverImage.url)}
                                        className={`${baseClassName}__hover-image`}
                                        alt={name || t('product.hoverImageAlt')}
                                        loading="lazy"
                                    />
                                )}
                            </div>
                        </ChooseLink>
                        <AddToCompare/>
                        <AddToWishlist/>
                    </div>

                    <div className={`${baseClassName}__center-line`}>
                        <ChooseLink link={linkUrl} product={product}>
                            <div
                                className={clsx(`${baseClassName}__details`, isCms && `${baseClassName}__details-slider`)}>
                                <strong className={`${baseClassName}__name ellipsis`}>{name}</strong>
                                {sku && (
                                    <div className={`${baseClassName}__sku ellipsis`}>
                                        <span>{t('productItem.sku')}</span>
                                        <span> {sku}</span>
                                    </div>
                                )}
                                <PriceBlock product={product} shortLabel={isMobile}/>
                            </div>
                        </ChooseLink>
                    </div>

                    {(!hideAddToCart || hasProductCard || hasEnergyLabels) && (
                        <div className={`${baseClassName}__bottom-line`}>
                            <div className={`${baseClassName}__bottom-info`}>
                                <div className={`${baseClassName}__bottom-labels`}>
                                    <Labels
                                        product={product}
                                        slot="TL"
                                        type="category"
                                        attributeCodePrefix="energetski_razred"
                                    />
                                </div>
                                <ProductDocumentLinks product={product}/>
                                {isHR && !hidePriceSkus.includes(product.sku) && <LastPrice30 product={product}/>}
                                <AnchorPrices product={product}/>
                            </div>
                            {!hideAddToCart && (
                                <div
                                    className={clsx(
                                        `${baseClassName}__cart-icon-wrap`,
                                        isCms && `${baseClassName}__cart-icon-wrap-slider`
                                    )}
                                >
                                    <AddToCartButton product={product}/>
                                </div>
                            )}
                        </div>
                    )}


                </div>
            </ProductProvider>
        </li>
    );
};

ProductItem.propTypes = {
    product: PropTypes.shape({
        name: PropTypes.string,
        canonical_url: PropTypes.string,
        url: PropTypes.string,
        image: PropTypes.string,
        thumbnailImage: PropTypes.shape({url: PropTypes.string}),
        hoverImage: PropTypes.shape({url: PropTypes.string}),
        sku: PropTypes.string,
        lifetimeStatus: PropTypes.string,
        isOnOrder: PropTypes.bool,
        days_to_delivery: PropTypes.number,
        guarantees: PropTypes.arrayOf(
            PropTypes.shape({
                guarantee_img_url: PropTypes.string,
                guarantee_description: PropTypes.string,
            })
        ),
        __typename: PropTypes.string,
    }),
    isCms: PropTypes.bool,
    isCategory: PropTypes.bool,
    isQuarticon: PropTypes.bool,
};

const areEqual = (prevProps, nextProps) => {
    // Re-render if product changed
    if (prevProps.product !== nextProps.product) {
        // Check if emlabels exist and are different
        const prevLabels = prevProps.product?.emlabels;
        const nextLabels = nextProps.product?.emlabels;

        // If one has labels and the other doesn't, re-render
        if (!prevLabels && nextLabels || prevLabels && !nextLabels) {
            return false;
        }

        // If both have labels, check if they're different
        if (prevLabels && nextLabels && prevLabels.length !== nextLabels.length) {
            return false;
        }

        // If SKU is the same, check other props
        if (prevProps.product?.sku === nextProps.product?.sku) {
            return prevProps.isCms === nextProps.isCms &&
                   prevProps.isCategory === nextProps.isCategory &&
                   prevProps.isQuarticon === nextProps.isQuarticon;
        }
    }

    // Default: re-render if props changed
    return prevProps.product === nextProps.product &&
           prevProps.isCms === nextProps.isCms &&
           prevProps.isCategory === nextProps.isCategory &&
           prevProps.isQuarticon === nextProps.isQuarticon;
};

export default React.memo(ProductItem, areEqual);
